import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Tooltip } from 'antd';
import { BulbOutlined } from '@ant-design/icons';

const BarcodeScannerModal = ({ visible, onClose, onDetected, title = 'Ler Código de Barras', cropPercent = 40, showCropMask = true }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const bufferRef = useRef('');
  const clearTimerRef = useRef(null);
  const canvasRef = useRef(null);
  const trackRef = useRef(null);
  const [torchOn, setTorchOn] = useState(false);
  const [canTorch, setCanTorch] = useState(false);
  const [error, setError] = useState(null);

  const stopStream = () => {
    try {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
      const stream = streamRef.current;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      trackRef.current = null;
      setTorchOn(false);
      setCanTorch(false);
    } catch { }
  };

  const toggleTorch = async () => {
    try {
      const track = trackRef.current || (streamRef.current && streamRef.current.getVideoTracks && streamRef.current.getVideoTracks()[0]);
      if (!track) return;
      const next = !torchOn;
      await track.applyConstraints({ advanced: [{ torch: next }] }).catch(() => { });
      setTorchOn(next);
    } catch { }
  };

  useEffect(() => {
    const handleKeydown = (e) => {
      if (!visible) return;
      // Ignora quando usuário está digitando em inputs
      const tag = (e.target && e.target.tagName) ? e.target.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea') return;

      // Barcode scanners normalmente enviam sequência rápida e terminam com Enter
      if (e.key === 'Enter') {
        const code = bufferRef.current.trim();
        bufferRef.current = '';
        if (clearTimerRef.current) {
          clearTimeout(clearTimerRef.current);
          clearTimerRef.current = null;
        }
        if (code) {
          onDetected && onDetected(code);
          try { stopStream(); } catch { }
          try { onClose && onClose(); } catch { }
          try {
            setTimeout(() => {
              const el = document.getElementById('txtchave') || document.activeElement;
              if (el) {
                try {
                  const evtDown = new KeyboardEvent('keydown', { key: 'Tab', code: 'Tab', keyCode: 9, which: 9, bubbles: true });
                  el.dispatchEvent(evtDown);
                } catch { }
                try { el.blur && el.blur(); } catch { }
                try {
                  const focusables = Array.from(document.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
                    .filter(node => !node.disabled && node.tabIndex >= 0 && node.offsetParent !== null);
                  const idx = focusables.indexOf(el);
                  const next = idx >= 0 ? (focusables[idx + 1] || null) : null;
                  if (next) { next.focus(); }
                } catch { }
                try {
                  const evtUp = new KeyboardEvent('keyup', { key: 'Tab', code: 'Tab', keyCode: 9, which: 9, bubbles: true });
                  el.dispatchEvent(evtUp);
                } catch { }
              }
            }, 0);
          } catch { }
        }
        return;
      }

      // Apenas caracteres imprimíveis
      if (e.key && e.key.length === 1) {
        bufferRef.current += e.key;
        // Limpa buffer se ficar ocioso por 1.5s
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
        clearTimerRef.current = setTimeout(() => { bufferRef.current = ''; }, 1500);
      }
    };

    if (visible) {
      (async () => {
        try {
          setError(null);
          // Solicita câmera traseira com resolução maior
          const constraints = {
            video: {
              facingMode: { ideal: 'environment' },
              width: { ideal: 1920 },
              height: { ideal: 1080 }
            },
            audio: false
          };
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          streamRef.current = stream;
          const videoEl = videoRef.current;
          if (videoEl) {
            videoEl.srcObject = stream;
            await videoEl.play();
          }

          try {
            const track = stream.getVideoTracks && stream.getVideoTracks()[0];
            const caps = track && track.getCapabilities ? track.getCapabilities() : {};
            if (track && caps) {
              trackRef.current = track;
              const adv = [];
              if (caps.focusMode && Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
                adv.push({ focusMode: 'continuous' });
              }
              if (caps.zoom) {
                const targetZoom = Math.min(2, caps.zoom.max || 2);
                adv.push({ zoom: targetZoom });
              }
              setCanTorch(!!caps.torch);
              if (adv.length > 0) {
                await track.applyConstraints({ advanced: adv }).catch(() => { });
              }
            }
          } catch { }

          // BarcodeDetector moderno
          const hasDetector = 'BarcodeDetector' in window;
          if (hasDetector) {
            const detector = new window.BarcodeDetector({
              formats: ['code_128']
            });
            if (!canvasRef.current) {
              canvasRef.current = document.createElement('canvas');
            }

            scanIntervalRef.current = setInterval(async () => {
              try {
                const video = videoRef.current;
                const canvas = canvasRef.current;
                if (!video || !canvas) return;
                const vw = video.videoWidth || 0;
                const vh = video.videoHeight || 0;
                if (!vw || !vh) return;
                const pct = Math.min(50, Math.max(30, Number(cropPercent) || 40));
                const bandH = Math.max(1, Math.floor(vh * (pct / 100)));
                const sy = Math.floor((vh - bandH) / 2);
                canvas.width = vw;
                canvas.height = bandH;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(video, 0, sy, vw, bandH, 0, 0, vw, bandH);
                let barcodes = await detector.detect(canvas);
                if (!barcodes || barcodes.length === 0) {
                  barcodes = await detector.detect(video);
                }
                if (barcodes && barcodes.length > 0) {
                  let best = barcodes[0];
                  try {
                    best = barcodes.reduce((a, b) => {
                      const aw = ((a.boundingBox && a.boundingBox.width) ? a.boundingBox.width : 0) * ((a.boundingBox && a.boundingBox.height) ? a.boundingBox.height : 0);
                      const bw = ((b.boundingBox && b.boundingBox.width) ? b.boundingBox.width : 0) * ((b.boundingBox && b.boundingBox.height) ? b.boundingBox.height : 0);
                      return bw > aw ? b : a;
                    }, barcodes[0]);
                  } catch { }
                  const value = best.rawValue || best.data || '';
                  if (value) {
                    onDetected && onDetected(value);
                    try { stopStream(); } catch { }
                    try { onClose && onClose(); } catch { }
                    try {
                      setTimeout(() => {
                        const el = document.getElementById('txtchave') || document.activeElement;
                        if (el) {
                          try {
                            const evtDown = new KeyboardEvent('keydown', { key: 'Tab', code: 'Tab', keyCode: 9, which: 9, bubbles: true });
                            el.dispatchEvent(evtDown);
                          } catch { }
                          try { el.blur && el.blur(); } catch { }
                          try {
                            const focusables = Array.from(document.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
                              .filter(node => !node.disabled && node.tabIndex >= 0 && node.offsetParent !== null);
                            const idx = focusables.indexOf(el);
                            const next = idx >= 0 ? (focusables[idx + 1] || null) : null;
                            if (next) { next.focus(); }
                          } catch { }
                          try {
                            const evtUp = new KeyboardEvent('keyup', { key: 'Tab', code: 'Tab', keyCode: 9, which: 9, bubbles: true });
                            el.dispatchEvent(evtUp);
                          } catch { }
                        }
                      }, 0);
                    } catch { }
                  }
                }
              } catch (err) {
                // Ignora erros transitórios
              }
            }, 250);
          } else {
            // Se BarcodeDetector não estiver disponível, exibe erro amigável
            setError('Seu navegador não suporta leitura por câmera. Conecte um leitor USB ou use Chrome/Edge modernos.');
          }
        } catch (err) {
          setError('Falha ao acessar a câmera: ' + (err?.message || String(err)));
        }
      })();
      window.addEventListener('keydown', handleKeydown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeydown);
      stopStream();
      bufferRef.current = '';
      if (clearTimerRef.current) {
        clearTimeout(clearTimerRef.current);
        clearTimerRef.current = null;
      }
    };
  }, [visible, onDetected]);

  useEffect(() => {
    if (!visible) {
      stopStream();
    }
  }, [visible]);

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      centered
      destroyOnHidden
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {error ? (
          <div style={{ color: '#ff4d4f' }}>{error}</div>
        ) : (
          <div style={{ position: 'relative', width: '100%', height: '60vh', minHeight: 400, background: '#000', borderRadius: 8, overflow: 'hidden' }}>
            <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'contain' }} playsInline muted />
            {canTorch ? (
              <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>
                <Tooltip title={torchOn ? 'Lanterna ligada' : 'Lanterna desligada'}>
                  <Button
                    type={torchOn ? 'primary' : 'default'}
                    shape="circle"
                    size="small"
                    onClick={toggleTorch}
                    icon={<BulbOutlined />}
                  />
                </Tooltip>
              </div>
            ) : null}
            {(() => {
              const pct = Math.min(50, Math.max(30, Number(cropPercent) || 40));
              const topPct = (100 - pct) / 2;
              return showCropMask ? (
                <div style={{ position: 'absolute', left: 0, width: '100%', top: `${topPct}%`, height: `${pct}%`, border: '2px dashed rgba(255,255,255,0.9)', borderRadius: 8, pointerEvents: 'none' }} />
              ) : null;
            })()}
          </div>
        )}
        <div style={{ fontSize: 12, color: '#666' }}>
          Aponte o código para a câmera. Se usar leitor USB, ele digitará automaticamente.
        </div>
      </div>
    </Modal>
  );
};

export default BarcodeScannerModal;